import { useMemo, VoidFunctionComponent } from 'react';
import { Text } from '@react-three/drei';
import { Image } from '@/components/Image';
import { helveticaNeue } from '@/lib/fonts';
import viewInARBanner from '@/img/view-in-ar-banner.png';
import androidArIcon from '@/img/android-ar-icon.png';
import appIcon from '@/img/onlybots-app-icon.png';
import cubeIcon from '@/img/cube-icon.png';
import { offblack } from '@/lib/colors';
import { deviceProperties } from '@/lib/deviceProperties';
import { ClickTarget } from '@/components/ClickTarget';

const BANNER_ASPECT = 0.291 / 2.24;
const BANNER_SCALAR = 2.25;
const BANNER_SCALE = [BANNER_SCALAR, BANNER_SCALAR * BANNER_ASPECT, 0];

interface Props {
    action: () => void;
}
const AndroidBanner: VoidFunctionComponent<Props> = ({ action }) => (
    <ClickTarget width={2.3} height={0.3} position={[0, 1.78, 2.1]} onClick={action}>
        <Image scale={BANNER_SCALE} transparent url={viewInARBanner} />
        <Text
            position={[-0.11, -0.018, 0.01]}
            letterSpacing={-0.04}
            anchorX="center"
            font={helveticaNeue}
            strokeColor="black"
            strokeWidth={1}
            fontSize={0.09}
            color={offblack}
        >
            View in AR
        </Text>
        <Image position={[0.25, 0, 0.01]} url={androidArIcon} transparent scale={0.2} />
    </ClickTarget>
);

const NonSafariBanner: VoidFunctionComponent<Props> = ({ action }) => (
    <ClickTarget width={2.3} height={0.3} position={[0, 1.78, 2.1]} onClick={action}>
        <Image scale={BANNER_SCALE} transparent url={viewInARBanner} />
        <group position={[-0.98, 0, 0.01]}>
            <Image url={appIcon} transparent scale={0.2} />
            <Text
                position={[0.46, -0.018, 0.01]}
                letterSpacing={-0.04}
                anchorX="center"
                font={helveticaNeue}
                strokeColor="black"
                strokeWidth={1}
                fontSize={0.09}
                color={offblack}
            >
                Onlybots VMO
            </Text>
        </group>
        <group position={[1, 0, 0.01]}>
            <Image url={cubeIcon} transparent scale={0.15} />
            <Text
                position={[-0.34, -0.018, 0.01]}
                letterSpacing={-0.04}
                anchorX="center"
                font={helveticaNeue}
                strokeColor="black"
                strokeWidth={1}
                fontSize={0.09}
                color={offblack}
            >
                View in AR
            </Text>
        </group>
    </ClickTarget>
);

interface MobileBannerProps {
    gltfUrl: string;
    name: string;
    tokenId: number;
}

export const MobileBanner: VoidFunctionComponent<MobileBannerProps> = ({ gltfUrl, name, tokenId }) => {
    const { isAndroid, isMobileSafari, isIOSDevice } = deviceProperties();

    const androidArViewerURL = useMemo(() => {
        const params = {
            file: gltfUrl,
            mode: 'ar_preferred',
            title: `${name} - #${tokenId}`,
            link: window.location.href,
        };
        const searchParams = new URLSearchParams(params);
        return `intent://arvr.google.com/scene-viewer/1.0?${searchParams.toString()}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=https://developers.google.com/ar;end;`;
    }, [gltfUrl, name, tokenId]);

    if (isMobileSafari) {
        return null;
    } else if (isAndroid) {
        return (
            <AndroidBanner
                action={() => {
                    window.location.href = androidArViewerURL;
                }}
            />
        );
    } else if (isIOSDevice) {
        return <NonSafariBanner action={() => (window.location.href = `/app/${tokenId}`)} />;
    }
    return null;
};
